import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  const navbarBg = isHome && !isScrolled
    ? 'bg-transparent'
    : 'bg-background-50/95 backdrop-blur-md';

  const textColor = isHome && !isScrolled
    ? 'text-background-50'
    : 'text-foreground-800';

  const logoColor = isHome && !isScrolled
    ? 'text-background-50'
    : 'text-primary-600';

  const linkHover = isHome && !isScrolled
    ? 'hover:text-primary-200'
    : 'hover:text-primary-500';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navbarBg} ${isScrolled ? 'shadow-sm border-b border-background-200/60' : ''}`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className={`flex items-center gap-2 ${logoColor} transition-colors`}>
            <div className="w-9 h-9 flex items-center justify-center">
              <i className="ri-book-read-line text-2xl"></i>
            </div>
            <span className="text-xl font-bold font-heading whitespace-nowrap">免费漫画网</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${linkHover} ${location.pathname === '/' ? (isHome && !isScrolled ? 'text-background-50 font-semibold' : 'text-primary-600 font-semibold') : ''}`}>
              首页
            </Link>
            <Link to="/category/all" className={`text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${linkHover} ${location.pathname.startsWith('/category') ? (isHome && !isScrolled ? 'text-background-50 font-semibold' : 'text-primary-600 font-semibold') : ''}`}>
              分类
            </Link>
            <Link to="/category/all?sort=rating" className={`text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${linkHover}`}>
              排行榜
            </Link>
            <Link to="/about" className={`text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${linkHover} ${location.pathname === '/about' ? (isHome && !isScrolled ? 'text-background-50 font-semibold' : 'text-primary-600 font-semibold') : ''}`}>
              关于我们
            </Link>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {/* Search */}
            {isSearchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索漫画..."
                  className="w-48 px-3 py-1.5 text-sm rounded-full bg-background-100 border border-background-300 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  autoFocus
                />
                <button type="button" onClick={() => setIsSearchOpen(false)} className="ml-2 text-foreground-500 hover:text-foreground-700 transition-colors">
                  <i className="ri-close-line"></i>
                </button>
              </form>
            ) : (
              <button
                onClick={() => setIsSearchOpen(true)}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${isHome && !isScrolled ? 'text-background-50 hover:bg-background-50/20' : 'text-foreground-600 hover:bg-background-200'}`}
                aria-label="搜索"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${isHome && !isScrolled ? 'text-background-50' : 'text-foreground-700'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 animate-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-4 space-y-3">
            <Link to="/" className="block py-2 text-sm font-medium text-foreground-800 hover:text-primary-500 whitespace-nowrap">
              首页
            </Link>
            <Link to="/category/all" className="block py-2 text-sm font-medium text-foreground-800 hover:text-primary-500 whitespace-nowrap">
              全部分类
            </Link>
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="block py-2 text-sm text-foreground-600 hover:text-primary-500 whitespace-nowrap"
              >
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="block py-2 text-sm font-medium text-foreground-800 hover:text-primary-500 whitespace-nowrap">
              关于我们
            </Link>
            <form onSubmit={handleSearch} className="pt-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索漫画..."
                  className="flex-1 px-3 py-2 text-sm rounded-full bg-background-100 border border-background-300 text-foreground-800 focus:outline-none focus:border-primary-400"
                />
                <button type="submit" className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 cursor-pointer">
                  <i className="ri-search-line"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
}